import React from 'react';
import { fmtMoney, priceSourceLabel } from '../../utils/priceMath';
import { manualLineView, centsToString } from '../../utils/tmPricing';

const money = (cents) => (cents === null || cents === undefined ? '—' : fmtMoney(centsToString(cents)));

/**
 * "Price by hand": one row per order line. Bill qty is the server's (create_invoice's own dry run),
 * read-only. Type a unit price, or a line total (it becomes a cents unit price plus the residual it
 * can't carry). A $0.00 line needs a no-charge reason; a line that bills 0 pieces is always $0.00.
 * previewLines (by so_line_id) shows what the last Preview priced; serverErrors (by so_line_id) are
 * the last Preview's 400 errors for that line, shown on its row.
 */
export default function TmManualLineTable({ lines, inputs, onChange, labelOf, previewLines = null, serverErrors = null, disabled = false }) {
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle small mb-2" data-testid="tm-manual-lines">
        <thead className="table-light">
          <tr>
            <th>Part</th>
            <th className="text-end" title="Quantity ordered">Ordered</th>
            <th className="text-end" title="Shipped (the cap on what can bill)">Shipped</th>
            <th className="text-end" title="Good pieces (net of defects)">Good</th>
            <th className="text-end" title="Pieces the close-out invoice will bill (the server's number)">Bill qty</th>
            <th className="text-end" title="Estimated unit when the quote was accepted (internal)">Est. unit</th>
            <th style={{ width: 120 }}>Unit price</th>
            <th style={{ width: 120 }} title="Optional: type a line total instead; it becomes a unit price">or line total</th>
            <th className="text-end">Extended</th>
          </tr>
        </thead>
        <tbody>
          {(lines || []).map((l) => {
            const input = inputs[l.so_line_id] || {};
            const v = manualLineView(l, input);
            const label = labelOf ? labelOf(l) : (l.description || `line ${l.so_line_id}`);
            const pv = previewLines ? previewLines[l.so_line_id] : null;
            const srv = (serverErrors && serverErrors[l.so_line_id]) || [];
            const flagged = !!v.error || srv.length > 0;
            return (
              <React.Fragment key={l.so_line_id}>
                <tr className={flagged ? 'table-warning' : ''}>
                  <td>
                    <div className="fw-semibold">{label}</div>
                    {pv && (
                      <div style={{ fontSize: '0.7rem' }} className="text-muted">
                        Preview: {pv.bill_qty} × {fmtMoney(pv.unit_price)} = {fmtMoney(pv.line_total)}
                        {pv.price_source && <span className="badge bg-success ms-1" style={{ fontSize: '0.6rem' }}>{priceSourceLabel(pv.price_source)}</span>}
                      </div>
                    )}
                  </td>
                  <td className="text-end">{l.quantity_ordered ?? '—'}</td>
                  <td className="text-end">{l.cap ?? '—'}</td>
                  <td className="text-end">{l.net_yield ?? '—'}</td>
                  <td className="text-end fw-bold">{v.billQty}</td>
                  <td className="text-end text-muted">{l.est_unit_at_acceptance != null ? fmtMoney(l.est_unit_at_acceptance) : '—'}</td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control form-control-sm text-end"
                      aria-label={`Unit price ${label}`}
                      value={v.zeroBill ? '0.00' : (input.mode === 'total' ? (v.unit || '') : (input.unit || ''))}
                      readOnly={v.zeroBill || input.mode === 'total'}
                      disabled={disabled}
                      onChange={(e) => onChange(l.so_line_id, { mode: 'unit', unit: e.target.value, total: '' })}
                      onFocus={() => { if (input.mode === 'total' && !v.zeroBill) onChange(l.so_line_id, { mode: 'unit', unit: v.unit || '', total: '' }); }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control form-control-sm text-end"
                      aria-label={`Line total ${label}`}
                      placeholder="optional"
                      value={input.mode === 'total' ? (input.total || '') : ''}
                      disabled={disabled || v.zeroBill}
                      onChange={(e) => onChange(l.so_line_id, { mode: 'total', total: e.target.value })}
                    />
                  </td>
                  <td className="text-end">
                    {money(v.extendedCents)}
                    {v.residualCents ? (
                      <div className="text-warning-emphasis" style={{ fontSize: '0.7rem' }} title="The typed total can't be split into whole cents per piece; this is what the unit price leaves over or under.">
                        residual {v.residualCents > 0 ? '+' : ''}{centsToString(v.residualCents)}
                      </div>
                    ) : null}
                  </td>
                </tr>
                {(v.needsReason || flagged) && (
                  <tr className={flagged ? 'table-warning' : ''}>
                    <td colSpan="9" className="pt-0">
                      {v.needsReason && (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          aria-label={`No-charge reason ${label}`}
                          placeholder={v.zeroBill ? 'This line bills 0 pieces — why? (no-charge reason)' : 'No-charge reason (why this line is $0.00)'}
                          value={input.reason || ''}
                          disabled={disabled}
                          onChange={(e) => onChange(l.so_line_id, { reason: e.target.value })}
                        />
                      )}
                      {v.error && <div className="text-danger" style={{ fontSize: '0.75rem' }}>{v.error}</div>}
                      {srv.map((t, i) => (
                        <div key={i} className="text-danger" style={{ fontSize: '0.75rem' }} data-testid={`tm-line-server-error-${l.so_line_id}`}>{t}</div>
                      ))}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
